import { useCallback, useMemo } from 'react';

import { SequencerDevice } from '@repo/sdk/models/components';

import { AtlasRouteIds } from '@/components/navigation/constants';
import { NavItemConfig } from '@/components/navigation/types';
import { useEquipmentDetailsRouteState } from '@/features/equipment-browser/equipment-details/hooks/use-equipment-details-route-state';
import { useResolvedEquipmentViewId } from '@/features/equipment-browser/hooks/use-resolved-equipment-view-id';
import { useGetSequencerDevices } from '@/hooks/query-hooks/gaia-api/agent-config';
import { useGetEquipmentBrowserConfig } from '@/hooks/query-hooks/gaia-api/equipment-browser';
import { useCurrentFacility } from '@/hooks/use-current-facility';

import { buildEquipmentNavSearch, resolveSystemViewId } from './equipment-nav-search';

export type NavItemNavConfig = {
  sequencers?: SequencerDevice[];
  useSequencerNav: boolean;
  equipmentNavSearch: Record<string, string> | undefined;
};

type NavConfig = {
  getNavItemConfig: (item: NavItemConfig) => NavItemNavConfig;
};

const getNordicSequencerDevices = (item: NavItemConfig, sequencerDevices?: SequencerDevice[]) => {
  const nordicSequencerAlias: { [key: string]: string[] } = {
    [AtlasRouteIds.CompressorSequencer]: ['htr_1', 'ltr_1'],
    [AtlasRouteIds.CondenserSequencer]: ['hpr_1'],
  };

  return (
    sequencerDevices?.filter(
      (device) => device.kind === 'vessel' && nordicSequencerAlias[item.id].includes(device.alias)
    ) || []
  );
};

export const useNavConfig = (): NavConfig => {
  const { agentId, orgUrlName } = useCurrentFacility();
  const { sectionId } = useEquipmentDetailsRouteState();
  const resolvedViewId = useResolvedEquipmentViewId();
  const { data: sequencerDevices } = useGetSequencerDevices();
  const { data: ebConfig } = useGetEquipmentBrowserConfig({ agentId });

  const systemViewId = useMemo(() => resolveSystemViewId(ebConfig?.views), [ebConfig?.views]);

  const getNavItemConfig = useCallback(
    (item: NavItemConfig): NavItemNavConfig => {
      let sequencers: SequencerDevice[] | undefined;
      let equipmentId: string | undefined;
      let useSequencerNav = false;

      if (
        item.id === AtlasRouteIds.CompressorSequencer ||
        item.id === AtlasRouteIds.CondenserSequencer
      ) {
        let resolvedSequencers: SequencerDevice[] = [];

        if (orgUrlName === 'nordic') {
          resolvedSequencers = getNordicSequencerDevices(item, sequencerDevices);
        } else {
          const deviceKind =
            item.id === AtlasRouteIds.CompressorSequencer
              ? 'compressor sequencer'
              : 'condenser sequencer';
          resolvedSequencers =
            sequencerDevices?.filter((device) => device.kind === deviceKind) || [];
        }

        if (resolvedSequencers.length > 0) {
          sequencers = resolvedSequencers;
          equipmentId = resolvedSequencers[0]?.id;
          useSequencerNav = resolvedSequencers.length > 1;
        }
      }

      const equipmentNavSearch = buildEquipmentNavSearch({
        routeId: item.id,
        viewId: resolvedViewId,
        sectionId,
        systemViewId,
        equipmentId,
      });

      return {
        sequencers,
        useSequencerNav,
        equipmentNavSearch,
      };
    },
    [orgUrlName, sequencerDevices, resolvedViewId, sectionId, systemViewId]
  );

  return {
    getNavItemConfig,
  };
};
